import React, { createContext, useContext, useEffect, useState } from 'react';

const AuthContext = createContext(null);
const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;

// Session-scoped auth storage (sessionStorage stays for the tab session,
// and we also enforce a 10-day expiry so an idle session eventually rotates).
// In production the actual OAuth Bearer token should live in an httpOnly
// cookie set by the backend — this SPA-side layer is only a lightweight
// cache of the user identity and access token expiry.
const KEY = 'pra_auth_v2';

function readStored() {
  try {
    const raw = sessionStorage.getItem(KEY) || localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) return null;
    return parsed;
  } catch { return null; }
}

function writeStored(payload) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(payload));
    // Also mirror to localStorage so a refresh keeps you in for up to 10 days
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {}
}

function clearStored() {
  sessionStorage.removeItem(KEY);
  localStorage.removeItem(KEY);
  // Clean up legacy keys from previous builds
  localStorage.removeItem('pra_user');
  localStorage.removeItem('pra_token');
}

export function AuthProvider({ children }) {
  const [state, setState] = useState(() => readStored() || { user: null, token: null, expiresAt: null });

  useEffect(() => {
    if (state.user && state.token) writeStored(state); else clearStored();
  }, [state]);

  const login = (user, token) => setState({ user, token, expiresAt: Date.now() + TEN_DAYS_MS });
  const logout = () => setState({ user: null, token: null, expiresAt: null });
  const setUser = (u) => setState(prev => ({ ...prev, user: u }));

  return (
    <AuthContext.Provider value={{ user: state.user, token: state.token, isAuthed: !!state.user, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
